import { useSession, useUser } from '@descope/react-sdk'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useOrgIdFromToken } from '@/lib/auth/orgid.hook'
import {
  composeTableStorageKey,
  getGroupedSource,
  writeStoredGrouped,
} from '@/lib/storage/local-storage.hook'
import { useBaseUrlParams } from '@/lib/url/base-url-param.hook'

import { CANONICAL_PARAM_KEYS } from '@/components/lemmala-design-system/table/consts'

export const groupedParamKey = (prefix: string): string =>
  `${prefix}_${CANONICAL_PARAM_KEYS.grouped}`

/**
 * Group-by toggle: URL wins, then localStorage, then defaultGrouped.
 * Ungroup stores the default (not a delete) so remount cannot revive grouped=true.
 */
export const usePersistedGrouped = ({
  prefix,
  urlParamKey,
  defaultGrouped = false,
  enabled = true,
}: {
  prefix?: string
  urlParamKey?: string
  defaultGrouped?: boolean
  enabled?: boolean
}): [boolean, (next: boolean) => void] => {
  const { urlParams, replaceParams } = useBaseUrlParams()
  const { user } = useUser()
  const { sessionToken } = useSession()
  const orgDetails = useOrgIdFromToken(user, sessionToken)
  const urlKey = enabled
    ? (urlParamKey ?? (prefix ? groupedParamKey(prefix) : undefined))
    : undefined
  const storageKey = composeTableStorageKey(prefix, user?.userId, orgDetails?.orgId)
  const persist = enabled && !!urlKey

  const [grouped, setGroupedState] = useState(() =>
    persist
      ? (getGroupedSource(storageKey, urlKey, urlParams).resolved ?? defaultGrouped)
      : defaultGrouped,
  )
  const didWriteBackRef = useRef(false)

  useEffect(() => {
    if (!persist || !urlKey || didWriteBackRef.current) return
    didWriteBackRef.current = true
    const { resolved } = getGroupedSource(storageKey, urlKey, urlParams)
    if (resolved === undefined || resolved === defaultGrouped || urlParams[urlKey]) return
    replaceParams({
      update: [[urlKey, resolved ? 'true' : 'false']],
      replaceCurrentHistoryEntry: true,
    })
  }, [persist, urlKey, storageKey, urlParams, replaceParams, defaultGrouped])

  const setGrouped = useCallback(
    (next: boolean) => {
      setGroupedState(next)
      if (!persist || !urlKey) return

      writeStoredGrouped(storageKey, next)
      if (next === defaultGrouped) {
        replaceParams({ remove: [urlKey], replaceCurrentHistoryEntry: true })
        return
      }

      replaceParams({
        update: [[urlKey, next ? 'true' : 'false']],
        replaceCurrentHistoryEntry: true,
      })
    },
    [persist, urlKey, storageKey, defaultGrouped, replaceParams],
  )

  return [grouped, setGrouped]
}
